import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { api } from "../lib/api";
import { HeaderNav } from "../components/HeaderNav";
import { Footer } from "../components/Footer";
import { ArrowLeft, Lock, Download } from "lucide-react";
import { RESUME_URL } from "../lib/data";

const Block = ({ label, children }) => {
  if (!children) return null;
  return (
    <div className="grid md:grid-cols-12 gap-6 py-10 border-t border-black/5" data-testid={`cs-block-${label.toLowerCase().replace(/\s/g, "-")}`}>
      <div className="md:col-span-3">
        <p className="overline text-primary">{label}</p>
      </div>
      <div className="md:col-span-8 md:col-start-5 text-lg text-foreground/70 leading-relaxed whitespace-pre-line">
        {children}
      </div>
    </div>
  );
};

export default function CaseStudy() {
  const { slug } = useParams();
  const [project, setProject] = useState(null);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
    api.getProject(slug).then(setProject).catch(() => setNotFound(true));
  }, [slug]);

  if (notFound) {
    return (
      <div className="min-h-screen flex flex-col">
        <HeaderNav />
        <div className="flex-1 flex flex-col items-center justify-center gap-4 px-6">
          <h1 className="font-serif text-3xl">Case study not found</h1>
          <Link to="/" className="text-primary hover:underline">← Back to portfolio</Link>
        </div>
        <Footer />
      </div>
    );
  }

  if (!project) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="h-8 w-8 rounded-full border-2 border-primary border-t-transparent animate-spin" />
      </div>
    );
  }

  return (
    <div className="grain relative">
      <HeaderNav />
      <article className="pt-28 pb-16">
        <div className="max-w-5xl mx-auto px-6">
          <Link to="/#projects" data-testid="cs-back-link" className="inline-flex items-center gap-2 text-sm text-foreground/60 hover:text-primary transition-colors mb-10">
            <ArrowLeft size={16} /> Back to projects
          </Link>

          <div className="flex flex-wrap gap-2 mb-5">
            {(project.tags || []).map((t) => (
              <span key={t} className="rounded-full bg-secondary text-primary font-mono text-[10px] uppercase tracking-widest px-2.5 py-1">
                {t}
              </span>
            ))}
          </div>

          <motion.h1
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="font-serif text-4xl md:text-6xl leading-[1.05] text-foreground max-w-3xl"
          >
            {project.title}
          </motion.h1>

          <p className="mt-6 text-xl text-foreground/70 max-w-2xl leading-relaxed">{project.summary}</p>

          <div className="mt-8 flex flex-wrap gap-x-10 gap-y-4">
            {project.role && (
              <div>
                <div className="overline text-primary/70">Role</div>
                <div className="text-foreground mt-1">{project.role}</div>
              </div>
            )}
            {project.tools?.length > 0 && (
              <div>
                <div className="overline text-primary/70">Tools</div>
                <div className="text-foreground mt-1">{project.tools.join(", ")}</div>
              </div>
            )}
          </div>

          {project.anonymized && (
            <div className="mt-8 inline-flex items-center gap-2 rounded-xl bg-secondary px-4 py-3 text-sm text-foreground/70">
              <Lock size={15} className="text-primary" />
              Details in this case study have been anonymized due to client confidentiality.
            </div>
          )}
        </div>

        {project.cover_image && (
          <div className="max-w-6xl mx-auto px-6 my-12">
            <img
              src={project.cover_image}
              alt={project.title}
              className="w-full rounded-2xl border border-black/5 object-cover max-h-[70vh]"
            />
          </div>
        )}

        <div className="max-w-5xl mx-auto px-6">
          <Block label="Overview">{project.overview}</Block>
          <Block label="Problem">{project.problem}</Block>
          <Block label="Process">{project.process}</Block>
          <Block label="Wireframes">{project.wireframes}</Block>
          <Block label="Final UI">{project.final_ui}</Block>
          <Block label="Impact">{project.impact}</Block>
          <Block label="Learnings">{project.learnings}</Block>
        </div>

        <div className="max-w-5xl mx-auto px-6 mt-16 flex flex-wrap items-center justify-between gap-6 border-t border-black/5 pt-10">
          <Link to="/#projects" className="text-primary hover:underline font-medium">← View more projects</Link>
          <a
            href={RESUME_URL}
            target="_blank"
            rel="noopener noreferrer"
            download
            className="inline-flex items-center gap-2 rounded-full bg-primary text-primary-foreground px-5 py-2.5 text-sm hover:bg-[hsl(242,47%,28%)] transition-colors"
          >
            <Download size={15} /> Download Resume
          </a>
        </div>
      </article>
      <Footer />
    </div>
  );
}
